<script lang="ts" setup>
import {
  computed,
  defineEmits,
  defineExpose,
  defineProps,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import { useRoute } from "vue-router";
import MarkdownIt from "markdown-it";
import { useBasicLayout } from "@/hooks/useBasicLayout";
import { ai_dictionary_Regexp_list } from "@/views/AIGenerate/smartReading/defaultData";
import { handleDecodeUrlComponentStr } from "@/views/AIGenerate/smartReading/commonMethod";
import { audioPlayStream } from "@/js/common";
import { getBrandImageUrl } from "@/js/brandImages";
import { useToggle } from "@vueuse/core";

interface Props {
  inversion?: boolean;
  error?: boolean;
  text?: string;
  loading?: boolean;
  asRawText?: boolean;
  propWord?: string;
  show?: boolean;
  learnType?: string;
  fontStyle?: Record<string, any>;
  articleText?: string;
  generateType?: string;
}

const emit = defineEmits(["clear"]);
const props = defineProps<Props>();
const route = useRoute();
const { isMobile } = useBasicLayout();
const gptStr = ref("");
const studentInfo = ref();
const audioPlayer = ref();
const mediaType = ref();
const imgTitle = getBrandImageUrl("ai_smart_reading/essay_title.png");
const textRef = ref<HTMLElement>();

const markdownRef2 = ref<any>();
const textBlack = ref<any>();
const bofangIcon = ref<Record<string, string>>({});
const timer = ref(0);
const dicObj = ref<Record<string, any>>({});
const [isExpand, changeIsExpand] = useToggle();
const expandHeight = ref("3rem");
const mdi = new MarkdownIt({
  html: false,
});
mdi.use(function (markdownit) {
  var defaultRender =
    markdownit.renderer.rules.text ||
    function (tokens, idx) {
      return tokens[idx].content;
    };
  markdownit.renderer.rules.text = function (tokens, idx) {
    var html = defaultRender(tokens, idx);
    html = html.replace(/^[^\u4e00-\u9fa5]+$/g, (str: string) => {
      str = str.replace(/\$(.+?)\$/g, '<span class="ai-strong">$&</span>');
      return `<div class="ai-text">${str}</div>`;
    });
    html = html.replace(/^语义分析.*/, `<img class="ai-title" src="${imgTitle}" />`);
    html = html.replaceAll("$", "");
    return html;
  };
});

const loading = computed(() => props.loading);

const wrapClass = computed(() => {
  return [
    "text-wrap",
    "min-w-[20px]",
    "rounded-md",
    isMobile.value ? "p-2" : "px-3 py-2",
    props.inversion ? "bg-[#d2f9d1]" : "bg-[#f4f6f8]",
    props.inversion ? "dark:bg-[#a1dc95]" : "dark:bg-[#1e1e20]",
    props.inversion ? "message-request" : "message-reply",
    { "text-red-500": props.error },
  ];
});
const text = computed(() => {
  // 给文章添加标题并首行居中显示
  let value;
  if (props.generateType === "vocabulary") {
    value = props.text ?? "";
  } else {
    value = "# " + props.text ?? "";
  }
  if (!props.asRawText) {
    return mdi.render(value);
  }
  return value;
});

function getEnByCn(cn: string) {
  const item = ai_dictionary_Regexp_list.find((i) => cn.includes(i.cn));
  if (item) {
    return item.en;
  } else {
    return "";
  }
}

function emitClickEvent(e: any) {
  let target = e.target;
  ai_dictionary_Regexp_list.forEach((item) => {
    if (target.id === `ai-audio-icon-${item.en}` && !loading.value) {
      streamAudioPlay(target.parentElement.innerText.replace(/.*[：|:]/gi, ""), target.id);
    }
  });
}

// 暂停播放
function pauseAudio(id?: string) {
  if (audioPlayer.value) {
    if (mediaType.value === "MEDIA_SOURCE") {
      audioPlayer.value.pause();
      audioPlayer.value = null;
    } else if (mediaType.value === "AUDIO_CONTEXT") {
      audioPlayer.value.close();
      audioPlayer.value = null;
    }
  }
  if (id) {
    bofangIcon.value[id] = "icon-bofang3";
    setElementClass(id, "icon-bofang3");
  } else {
    bofangIcon.value = {};
  }
  clearInterval(timer.value);
}

// 设置播放按钮元素类名
function setElementClass(id: string, value: string) {
  const el = document.querySelector(`#${id}`);
  const classList = el?.classList;
  classList?.remove("icon-bofang3", "icon-bofang2", "icon-bofang1");
  classList?.add(value);
}

// 播放音频
function streamAudioPlay(txt: string, id: string) {
  // 关闭音频
  pauseAudio(id);

  audioPlayStream(txt, (callbackData: any, type: string) => {
    mediaType.value = type;
    let num = 0;
    if (mediaType.value === "MEDIA_SOURCE") {
      audioPlayer.value = new Audio(callbackData);
      audioPlayer.value.oncanplay = function () {
        audioPlayer.value.play();
      };
      audioPlayer.value.onended = function () {
        console.log("ended");
        pauseAudio(id);
      };
      audioPlayer.value.oncanplaythrough = () => {
        console.log("loading");
        clearInterval(timer.value);
        timer.value = window.setInterval(() => {
          num = ++num % 3;
          bofangIcon.value[id] = `icon-bofang${num + 1}`;
          setElementClass(id, `icon-bofang${num + 1}`);
        }, 300);
      };
    } else if (mediaType.value === "AUDIO_CONTEXT") {
      audioPlayer.value = callbackData;
    }
  });
}

function clearFn() {
  emit("clear");
}

function changeExpandFn() {
  changeIsExpand();
  const a = document.querySelector(".essay-center-title");
  if (a) {
    a.classList.remove("expand-class-active");
    if (isExpand.value) {
      a.style.height = a?.scrollHeight + "px";
      expandHeight.value = a?.scrollHeight + "px";
      a.classList.add("expand-class-active");
    } else {
      a.style.height = "3rem";
      expandHeight.value = "3rem";
    }
  }
}

onMounted(() => {
  //获取路由参数
  studentInfo.value = handleDecodeUrlComponentStr(route.params.studentInfo);
  console.log(dicObj.value);
});

onBeforeUnmount(() => {
  pauseAudio();
});

//对外部暴露函数
defineExpose({
  textBlack,
});
</script>

<template>
  <div class="text-black" :class="wrapClass">
    <div ref="textRef" class="leading-relaxed break-words">
      <div ref="textBlack" :scroll-events="true" :overflow-scroll="true" :scroll="true">
        <div class="markdown-body3">
          <div class="essay-center">
            <div class="essay-center-title">
              <span class="essay-center-title_label">原文：</span>{{ props.propWord }}
            </div>
            <div class="essay-center-img">
              <img
                class="essay-center-img_label"
                :src="getBrandImageUrl('ai_smart_reading/essay_button.svg')"
                alt=""
                @click="clearFn"
              />
            </div>
            <img
              :src="getBrandImageUrl('ai_smart_reading/arrow.png')"
              class="essay-center-icon"
              :class="[isExpand ? 'expand-class' : 'not-expand-class']"
              alt=""
              @click="changeExpandFn"
            />
          </div>
          <div ref="markdownRef2" class="essay-box" v-html="text"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.ai-sentence {
  //background-color: #1f6feb;
}

html.plt-mobile ion-app {
  -webkit-user-select: auto !important;
  -moz-user-select: auto !important;
  -ms-user-select: auto !important;
  user-select: auto !important;
}

ion-content::part(background) {
  background-color: transparent;
}

.flex-row {
  display: flex;
  flex-direction: row;
}

.text-black {
  //position: relative;
  font-size: inherit;
  display: flex;
  width: 100%;
  height: 100%;

  .cursor-pointer {
    cursor: pointer;
  }

  .handle-methods {
    z-index: 1;
    position: absolute;
    top: var(--currentX);
    left: var(--currentY);
  }
}

.leading-relaxed {
  width: 100%;
}

.markdown-body3 {
  background-color: #ffffff;
  font-size: inherit;
  width: 100%;
  line-height: 1.5rem;
  height: 100%;
  border: 0.15rem solid #e0e8f6;
  border-radius: 1rem;

  &::part(scroll) {
    width: calc(100% + 4vw);
    height: 100%;
    display: flex;
    flex-direction: column;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-right: 4vw;
  }

  .essay-center {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 1rem;
    background: #f7fafe;
    border-bottom: 0.15rem solid #e0e8f6;
    border-radius: 1rem 1rem 0 0;
    position: relative;

    &-title {
      color: #606266;
      flex: 1;
      font-weight: 400;
      font-size: 0.8rem;
      line-height: 1.5rem;
      overflow: hidden;
      transition: height 0.2s ease-in-out;
      height: 3rem;

      &_label {
        color: #262626;
        font-size: 0.9rem;
        font-weight: 500;
      }
    }

    &-img {
      display: flex;
      justify-content: center;
      align-items: flex-start;

      &_label {
        width: 3rem;
      }
    }

    &-icon {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);

      &.expand-class {
        transform: rotate(180deg);
      }

      &.not-expand-class {
        transform: rotate(0);
      }
    }
  }

  .essay-box {
    padding: 1rem;
  }

  .ai-empty {
    margin-bottom: -2rem;
    height: 0;
    display: block;
    font-size: 0;
  }

  .ai-text {
    color: #262626;
    font-size: 1rem;
    line-height: 2rem;
    font-weight: 400;
    text-indent: 2rem;
    margin-bottom: 2rem;
  }

  .ai-title {
    height: 1.8rem;
  }

  .ai-strong {
    background: #ffad004d;
    border: 0.15rem dotted #ffad00;
  }

  h1 {
    text-align: center;
    font-size: var(--font-3-point-5);
    font-weight: 700;
    padding: 0 1rem;
    white-space: pre-wrap;

    strong {
      color: #262626 !important;
      font-weight: 700;
    }
  }

  strong {
    font-weight: 400;
  }

  p {
    margin: 0;
    color: #606266;
    font-size: 1rem;
  }

  ol {
    list-style-type: decimal;
    padding: 0;

    li {
      font-size: 1rem;
    }
  }

  ul {
    margin: 2rem 0;
    padding-left: 0;
  }

  li {
    list-style: none;
    color: #606266;
  }

  li li {
    list-style-type: square;
  }

  caption {
    caption-side: bottom;
    padding: 10px;
    font-weight: bold;
  }

  pre code,
  pre tt {
    line-height: 1.65;
  }

  .highlight pre,
  pre {
    background-color: #fff;
  }

  code.hljs {
    padding: 0;
  }
}

.common-markdown {
  p {
    text-indent: 0;
  }
}

html.dark {
  .message-reply {
    .whitespace-pre-wrap {
      white-space: pre-wrap;
      color: var(--n-text-color);
    }
  }

  .highlight pre,
  pre {
    background-color: #282c34;
  }
}

@keyframes expand-animation {
  16.65% {
    height: calc(v-bind(expandHeight) + 1rem);
  }
  33.3% {
    height: calc(v-bind(expandHeight) - 0.8rem);
  }
  49.95% {
    height: calc(v-bind(expandHeight) + 0.4rem);
  }
  66.6% {
    height: calc(v-bind(expandHeight) - 0.2rem);
  }
  83.25% {
    height: calc(v-bind(expandHeight) + 0.1rem);
  }
  100% {
    height: v-bind(expandHeight);
  }
}

.expand-class-active {
  animation-name: expand-animation;
  animation-duration: 0.6s;
  animation-timing-function: ease-in-out;
  animation-iteration-count: 1;
}
</style>
